import { Component, HostBinding, Input, OnInit } from '@angular/core';
import { ChartService } from '@app/data/services/chartService';
import { AggregateCartesianChartConfig, BasicChartDataSource } from '@app/data/interface/widget-config';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import moment from 'moment-timezone';
import { EChartsOption, LineSeriesOption, SeriesOption } from 'echarts';
import { FilterService } from '@app/data/services/filterService';
import { SitesService } from '@app/data/services/sitesService';
import { BaseFilteredEnvironmentalDataChartComponent } from '../base-filtered-environmental-data-chart-component';
import { forceChartOptionsRerender } from '../helpers/widget-render.utils';
import { buildEchartsConfigFromDataSources } from '@app/widgets/helpers/widgets';

@Component({
  selector: 'app-aggregate-cartesian-chart',
  templateUrl: './aggregate-cartesian-chart.component.html',
  styleUrls: ['./aggregate-cartesian-chart.component.scss'],
})
export class AggregateCartesianChartComponent extends BaseFilteredEnvironmentalDataChartComponent {
  @Input() config!: AggregateCartesianChartConfig;
  @Input() chartOptions: EChartsOption | null = null;
  private _doubleWidth: boolean = false;

  @HostBinding('class.double-width')
  get hostDoubleWidth(): boolean {
    return this.doubleWidth;
  }

  @Input()
  set doubleWidth(value: boolean | string | null | undefined) {
    this._doubleWidth = !!value && value !== 'false';
  }

  get doubleWidth(): boolean {
    return this._doubleWidth;
  }

  ngOnInit() {
    super.ngOnInit();
    this.id = `aggregate-cartesian-chart-${this.config.dataConfig.widgetId}`;
    this.chartTitle = this.config.dataConfig.title || '';
    this.chartSubtitle = this.config.dataConfig.subtitle || '';
    this.domain = this.config.dataConfig.domain || '';

    // First load.
    this.setWidgetData();
  }

  /**
   * Clear any displayed chart data so the UI does not show stale results while
   * new data is being fetched. Implements the abstract contract from the base class.
   */
  protected clearChartData(): void {
    this.chartOptions = null;
    this.hasData = false;
  }

  createIdToLabelMapping(queryResponses: any): Record<string, string> {
    const allSources: any[] = [];
    (queryResponses || []).forEach((resp: any) => {
      if (!resp || !Array.isArray(resp.environmentalDataSources)) return;
      resp.environmentalDataSources.forEach((src: any) => {
        if (!src) return;
        const id = src.id || src.source_identifier;
        if (!id) return;
        if (!allSources.find((s: any) => (s.id || s.source_identifier) === id)) allSources.push(src);
      });
    });

    return allSources.reduce((acc: any, src: any) => {
      if (!src) return acc;
      const id = src.id || src.source_identifier;
      const label = src.human_readable_name || src.source_identifier || src.id || `${id}`;
      if (id) acc[id] = label;
      return acc;
    }, {});
  }

  /**
   * Builds the selected sources for the mapper.
   * @param aggregateSource The aggregate source to build from.
   * @param idToLabelMap The mapping of IDs to labels.
   * @returns An array of selected sources or undefined.
   */
  private buildSelectedSourcesForMapper(
    aggregateSource: BasicChartDataSource,
    idToLabelMap: Record<string, string>
  ): BasicChartDataSource[] | undefined {
    const identifiers = this.lastAppliedEnvironmentalDataSourceIdentifiers;
    if (!Array.isArray(identifiers) || identifiers.length === 0) return undefined;

    return identifiers.map((identifier) => {
      const matchingEnvSource = (this.allEnvironmentalDataSources ?? []).find(
        (source) => source.source_identifier === identifier
      );

      return {
        environmentalDataSourceIdentifier: identifier,
        analyteIdentifier: aggregateSource.analyteIdentifier,
        valueField: aggregateSource.valueField,
        label:
          idToLabelMap[identifier] ?? matchingEnvSource?.human_readable_name ?? aggregateSource.label ?? identifier,
      };
    });
  }

  protected override selectDataSources(
    sources: BasicChartDataSource[],
    options?: { preserveConfiguredLabels?: boolean }
  ): BasicChartDataSource[] {
    const selection = super.selectDataSources(sources, options);
    const aggregateConfigSources = sources.filter((source) => source.environmentalDataSourceIdentifier === 'all');
    if (aggregateConfigSources.length === 0) return selection;

    const configNonAggregateIdentifiers = new Set(
      sources
        .filter((source) => source.environmentalDataSourceIdentifier !== 'all')
        .map((source) => source.environmentalDataSourceIdentifier)
    );

    const result: BasicChartDataSource[] = [];

    selection.forEach((source) => {
      if (configNonAggregateIdentifiers.has(source.environmentalDataSourceIdentifier)) {
        result.push(source);
      }
    });

    aggregateConfigSources.forEach((aggregateSource) => {
      const alreadyIncluded = result.some(
        (source) => source.environmentalDataSourceIdentifier === aggregateSource.environmentalDataSourceIdentifier
      );
      if (!alreadyIncluded) {
        result.push({ ...aggregateSource });
      }
    });

    return result;
  }

  /**
   * Maps the data for the series.
   *
   * This method can be overridden via the dataConfig to provide custom mapping logic.
   * @param keyedBySourceId The data keyed by source ID.
   * @param idToLabelMap The mapping of IDs to labels.
   * @param xAxisData The data for the X-axis.
   * @param legendLabels The labels for the legend.
   * @returns The series options.
   */
  mapDataToSeries(
    keyedBySourceId: Record<string, Record<string, any>>,
    idToLabelMap: Record<string, string>,
    xAxisData: any[],
    legendLabels: string[]
  ): SeriesOption[] {
    return Object.keys(keyedBySourceId).map((sid) => {
      const dataMap = keyedBySourceId[sid] || {};
      const readable = idToLabelMap[sid] || sid;
      const seriesOpt: SeriesOption = {
        type: 'line',
        name: readable,
        data: xAxisData.map((dateTimeValue: string) =>
          dataMap.hasOwnProperty(dateTimeValue) ? dataMap[dateTimeValue] : null
        ),
      } as SeriesOption;
      legendLabels.push(String(readable));
      return seriesOpt;
    });
  }

  /**
   * Build axis labels, formatting as dates if requested.
   * @param values The values to format.
   * @param shouldFormatAsDate Whether to format the values as dates.
   * @param addPadding Whether to add padding to the labels.
   * @returns The formatted axis labels.
   */
  private buildAxisLabels(values: any[], shouldFormatAsDate: boolean, addPadding = false): string[] {
    const formatted = values.map((v: string) => {
      if (!shouldFormatAsDate) return String(v ?? '');
      const m = moment(v);
      const dateFormat = this.startDate === this.endDate ? 'HH:00' : 'DD/MM/yyyy';
      return m.isValid() ? m.format(dateFormat) : String(v ?? '');
    });

    if (!addPadding || values.length === 0) return formatted;

    // When padding is requested, caller may want empty slots at ends — we leave caller to decide exact indexes
    return ['', ...formatted, ''];
  }

  /**
   * Ensures the Y-axis is treated as a categorical axis.
   * @param opts The ECharts options.
   * @param axisLabels The labels to use for the Y-axis.
   */
  private ensureYAxisIsCategory(opts: EChartsOption, axisLabels: string[]) {
    if (!Array.isArray(opts.yAxis) || opts.yAxis.length === 0) {
      opts.yAxis = [{}];
    }
    const yAxisOption: any = opts.yAxis[0] || {};
    yAxisOption.type = yAxisOption.type || 'category';
    yAxisOption.data = axisLabels;
    opts.yAxis[0] = yAxisOption;
  }

  /**
   * Ensures the X-axis is treated as a value axis.
   * @param opts The ECharts options.
   */
  private ensureXAxisIsValue(opts: EChartsOption) {
    opts.xAxis = {
      ...(opts.xAxis || {}),
      type: opts.xAxis && (opts.xAxis as any).type ? (opts.xAxis as any).type : 'value',
    } as any;
  }

  /**
   * Apply categorical axis options to chart options.
   *
   * Allows choosing which axis is categorical, and handles date formatting.
   *
   * @param opts
   * @param categoricalAxis
   * @param xAxisData
   * @param shouldFormatXAxisAsDate
   * @param dateFormat
   * @param addPadding
   * @returns
   */
  private applyCategoricalAxisOptions(
    opts: EChartsOption,
    categoricalAxis: 'x' | 'y',
    xAxisData: any[],
    shouldFormatXAxisAsDate: boolean,
    addPadding: boolean,
    showBoundary?: boolean,
    boundary?: number
  ) {
    if (categoricalAxis === 'y') {
      const axisLabels = this.buildAxisLabels(xAxisData, shouldFormatXAxisAsDate, false);
      this.ensureYAxisIsCategory(opts, axisLabels);
      this.ensureXAxisIsValue(opts);
      return;
    }

    const labels = this.buildAxisLabels(xAxisData, shouldFormatXAxisAsDate, addPadding);
    const existingXAxis = Array.isArray(opts.xAxis) ? (opts.xAxis[0] as any) ?? {} : (opts.xAxis as any) ?? {};
    const otherAxes = Array.isArray(opts.xAxis) ? (opts.xAxis as any[]).slice(1) : [];
    const computedLabels = labels.map((value: string, index: number) => {
      if (addPadding && (index === 0 || index === labels.length - 1)) return '';
      return value;
    });

    const updatedXAxis = {
      ...existingXAxis,
      type: existingXAxis.type || 'category',
      boundaryGap: showBoundary,
      boundary: boundary,
      data: computedLabels,
    } as any;

    opts.xAxis = otherAxes.length ? [updatedXAxis, ...otherAxes] : updatedXAxis;
  }

  /**
   * Set a default "no data" chart options payload.
   * @param message Optional title text to show on the chart.
   */
  private setNoDataChartOptions(): void {
    const text = 'No data is available for the selected date range.';
    this.hasData = false;
    this.chartOptions = {
      color: ['#6AB6B0'],
      title: {
        text,
      },
    };
  }

  /**
   * Add padding entries to the X axis (one before/one after) using hour/day units.
   * If extraAfter is true, add an additional entry after the last one.
   */
  private adjustXAxisForSinglePoint(xAxisData: any[], isSameDate: boolean, extraAfter: boolean) {
    if (!Array.isArray(xAxisData)) return;

    if (isSameDate) {
      // add one hour after
      xAxisData.push(
        moment(xAxisData[xAxisData.length - 1])
          .tz('UTC')
          .add(1, 'hour')
          .format('YYYY-MM-DDTHH:00:00.000\\Z')
      );
      // add one hour before
      xAxisData.unshift(moment(xAxisData[0]).add(-1, 'hour').tz('UTC').format('YYYY-MM-DDTHH:00:00\\Z'));

      if (extraAfter) {
        // add another hour after the current last
        xAxisData.push(
          moment(xAxisData[xAxisData.length - 1])
            .tz('UTC')
            .add(1, 'hour')
            .format('YYYY-MM-DDTHH:00:00.000\\Z')
        );
      }
    } else {
      // add one day after
      xAxisData.push(
        moment(xAxisData[xAxisData.length - 1])
          .tz('UTC')
          .add(1, 'day')
          .format('YYYY-MM-DDT00:00:00.000\\Z')
      );
      // add one day before
      xAxisData.unshift(moment(xAxisData[0]).add(-1, 'day').tz('UTC').format('YYYY-MM-DDT00:00:00\\Z'));

      if (extraAfter) {
        // add another day after the current last
        xAxisData.push(
          moment(xAxisData[xAxisData.length - 1])
            .tz('UTC')
            .add(1, 'day')
            .format('YYYY-MM-DDT00:00:00.000\\Z')
        );
      }
    }
  }

  keyDataBySourceId(seriesData: any, dataSource: BasicChartDataSource) {
    const keyedBySourceId: Record<string, Record<string, any>> = {};
    (seriesData.data || []).forEach((dataPoint: any) => {
      if (!dataPoint || !dataPoint.date_time) return;
      const sid = dataPoint.environmental_data_source_id || dataPoint.environmentalDataSourceId || dataPoint.source_id;
      const sourceId = sid != null ? String(sid) : 'unknown';
      const rawVal = dataPoint[dataSource.valueField];
      let formattedVal: any = rawVal;
      if (rawVal != null && typeof rawVal === 'number') {
        const sigFigs =
          seriesData && seriesData.analytes && seriesData.analytes[0]
            ? seriesData.analytes[0].significant_figures
            : undefined;
        if (sigFigs) {
          try {
            formattedVal = rawVal.toPrecision(sigFigs);
          } catch (e) {
            formattedVal = rawVal;
          }
        }
      }
      keyedBySourceId[sourceId] = keyedBySourceId[sourceId] || {};
      keyedBySourceId[sourceId][dataPoint.date_time] = formattedVal;
    });
    return keyedBySourceId;
  }

  /**
   * Handle query responses and update chart options accordingly.
   *
   * Supports applying categorical axis options, date formatting, and data mapping.
   * @param queryResponses
   * @param categoricalAxis
   * @param shouldFormatXAxisAsDate
   * @param addPadding
   * @param newChartOptions
   * @param xAxisData
   * @param applicableDataSources
   * @returns
   */
  private handleQueryResponses(
    queryResponses: any,
    categoricalAxis: any,
    shouldFormatXAxisAsDate: boolean,
    addPadding: boolean,
    newChartOptions: EChartsOption,
    xAxisData: any[],
    applicableDataSources: BasicChartDataSource[]
  ) {
    const idToLabelMap: Record<string, string> = this.createIdToLabelMapping(queryResponses);
    let rawDataFound = false;

    queryResponses.forEach((seriesData: any) => {
      if (!seriesData || !Array.isArray(seriesData.data)) return;

      const data = seriesData.data;
      if (data.length) rawDataFound = true;

      const newDates = data
        .map((dataPoint: any) => dataPoint && dataPoint.date_time)
        .filter((dateTime: any) => !!dateTime && !xAxisData.includes(dateTime));

      if (newDates.length) xAxisData.push(...newDates);
    });

    if (!rawDataFound) {
      this.setNoDataChartOptions();
      return;
    }

    if (categoricalAxis === 'x' && xAxisData.length === 1) {
      this.adjustXAxisForSinglePoint(xAxisData, this.startDate === this.endDate, false);
    }

    if (categoricalAxis === 'x' && this.config.dataConfig.addPadding === true) {
      this.adjustXAxisForSinglePoint(
        xAxisData,
        this.startDate === this.endDate,
        !!this.config.dataConfig.addPaddingAfter
      );
    }
    if (categoricalAxis === 'x' && shouldFormatXAxisAsDate) {
      xAxisData.sort((a: string, b: string) => (a > b ? 1 : -1));
    }

    const legendLabels: string[] = [];
    queryResponses.forEach((seriesData: any, index: number) => {
      const dataSource: BasicChartDataSource = applicableDataSources[index];
      const targetSeries = (newChartOptions.series as SeriesOption[])[index];
      if (!seriesData || !Array.isArray(seriesData.data)) {
        targetSeries && (targetSeries.data = xAxisData.map(() => null));
        return;
      }

      const sigFigs =
        seriesData && seriesData.analytes && seriesData.analytes[0]
          ? seriesData.analytes[0].significant_figures
          : undefined;

      const keyedData = seriesData.data.reduce((accumulator: any, dataPoint: any) => {
        if (!dataPoint || !dataPoint.date_time) return accumulator;
        const rawVal = dataPoint[dataSource?.valueField];
        let formattedVal: any = rawVal;
        if (rawVal != null && typeof rawVal === 'number' && sigFigs) {
          try {
            formattedVal = rawVal.toPrecision(sigFigs);
          } catch (e) {
            formattedVal = rawVal;
          }
        }
        return {
          [dataPoint.date_time]: formattedVal,
          ...accumulator,
        };
      }, {});

      if (dataSource?.environmentalDataSourceIdentifier === 'all') {
        const keyedBySourceId: Record<string, Record<string, any>> = this.keyDataBySourceId(seriesData, dataSource);

        const baseSeries: SeriesOption[] = Array.isArray(newChartOptions.series)
          ? (newChartOptions.series as SeriesOption[])
          : [];

        const mapper = this.config.dataConfig.mapDataToSeries || this.mapDataToSeries.bind(this);

        const selectedSourcesForMapper = this.buildSelectedSourcesForMapper(dataSource, idToLabelMap);
        const newSeriesForSources: SeriesOption[] = mapper(
          keyedBySourceId,
          idToLabelMap,
          xAxisData,
          legendLabels,
          dataSource,
          seriesData,
          selectedSourcesForMapper
        );

        newChartOptions.series = [...baseSeries, ...newSeriesForSources];
      } else {
        const seriesArray = newChartOptions.series as SeriesOption[];
        if (seriesArray.length <= index) return;
        const targetSeries = seriesArray[index];
        if (!targetSeries) return;

        seriesArray[index].data = xAxisData.map(
          (dateTimeValue: string) =>
            keyedData[dateTimeValue] ||
            (targetSeries?.type === 'line' &&
            targetSeries?.stack === 'Total' &&
            seriesData.data.length < xAxisData.length
              ? 0
              : null)
        );
        legendLabels[index] = String((newChartOptions.series as SeriesOption[])[index].name || dataSource.label || '');
      }
    });

    if (categoricalAxis === 'x' && (!Array.isArray(xAxisData) || xAxisData.length === 0)) {
      this.setNoDataChartOptions();
      return;
    }

    if (categoricalAxis === 'y') {
      const axisLabels = this.buildAxisLabels(xAxisData, shouldFormatXAxisAsDate, false);
      const meaningfulAxisLabels = axisLabels.filter((l) => l != null && String(l).trim() !== '');
      if (meaningfulAxisLabels.length === 0) {
        this.setNoDataChartOptions();
        return;
      }
    }

    const postMappingSeries: SeriesOption[] = Array.isArray(newChartOptions.series)
      ? (newChartOptions.series as SeriesOption[])
      : [];
    const hasPlotData = postMappingSeries.some(
      (s) =>
        Array.isArray((s as any).data) &&
        (s as any).data.some((v: any) => v !== null && v !== undefined && !(typeof v === 'number' && isNaN(v)))
    );
    if (!hasPlotData) {
      this.setNoDataChartOptions();
      return;
    }

    this.applyCategoricalAxisOptions(
      newChartOptions,
      categoricalAxis as 'x' | 'y',
      xAxisData,
      shouldFormatXAxisAsDate,
      addPadding,
      this.config.dataConfig.showBoundary,
      this.config.dataConfig.boundary
    );

    if ((newChartOptions as any) && (newChartOptions as any).legend === undefined) (newChartOptions as any).legend = {};
    {
      const optsAny = newChartOptions as any;
      optsAny.legend = optsAny.legend || {};
      if (!Array.isArray(optsAny.legend.data)) {
        const deduped = Array.from(new Set((legendLabels || []).filter(Boolean).map(String)));
        optsAny.legend.data = deduped;
      }
    }

    this.hasData = true;
    this.chartOptions = newChartOptions;
  }

  /**
   * Set the widget data by fetching from the chart service and processing responses.
   */
  setWidgetData() {
    const categoricalAxis = this.config.dataConfig.categoricalAxis ?? 'x';
    const shouldFormatXAxisAsDate = this.config.dataConfig.formatXAxisAsDate !== false;
    const addPadding = Boolean(this.config.dataConfig.addPadding);
    const newChartOptions: EChartsOption = { ...(this.config.echartsConfig || {}) } as EChartsOption;
    newChartOptions.series = Array.isArray(this.config.echartsConfig?.series)
      ? [...(this.config.echartsConfig!.series as SeriesOption[])]
      : [];

    const xAxisData: any[] = [];
    this.hasData = false;
    this.chartOptions = null;

    // If we don't have any data sources to look at, exit early.
    const preserveConfiguredLabels = Boolean(this.config?.dataConfig?.preserveConfiguredDataSourceLabels);
    const applicableDataSources = this.selectDataSources(this.config.dataConfig.dataSources, {
      preserveConfiguredLabels,
    });
    console.debug('[AggregateCartesianChartComponent] setWidgetData selectDataSources result', {
      configSourceCount: this.config.dataConfig.dataSources?.length ?? 0,
      applicableCount: applicableDataSources.length,
      selectedSources: this.selectedEnvironmentalDataSources,
      selectedGroups: this.selectedEnvironmentalDataSourceDashGroups,
      allEnvironmentalDataSourcesCount: this.allEnvironmentalDataSources?.length ?? 0,
    });

    // If there's nothing to display....
    if (applicableDataSources.length === 0) {
      console.debug('[AggregateCartesianChartComponent] No applicable data sources found; aborting data load.', {
        title: this.chartTitle || this.config?.dataConfig?.title || null,
        widgetId: this.config?.dataConfig?.widgetId || null,
        domain: this.domain || this.config?.dataConfig?.domain || null,
        allEnvironmentalDataSourcesCount: this.allEnvironmentalDataSources?.length ?? 0,
      });
      this.setNoDataChartOptions();
      return;
    }

    console.debug(
      '[AggregateCartesianChartComponent] applicableDataSources identifiers:',
      applicableDataSources.map((s) => s.environmentalDataSourceIdentifier)
    );

    forkJoin(
      applicableDataSources.map((source: BasicChartDataSource) => {
        console.debug(
          `[AggregateCartesianChartComponent] requesting data for source="${
            source.environmentalDataSourceIdentifier
          }" analyte="${source.analyteIdentifier}" entityNamesCount=${(source.entityNames || []).length}`
        );

        return this.chartService
          .getEnvironmentalDataForSourceAndAnalyteByDate(
            source.environmentalDataSourceIdentifier,
            source.analyteIdentifier,
            moment(this.startDate).format('YYYY-MM-DD'),
            moment(this.endDate).format('YYYY-MM-DD'),
            this.startDate === this.endDate ? 'hour' : 'day',
            source.entityNames || []
          )
          .pipe(take(1));
      })
    ).subscribe({
      next: (queryResponses: any) =>
        this.handleQueryResponses(
          queryResponses,
          categoricalAxis,
          shouldFormatXAxisAsDate,
          addPadding,
          newChartOptions,
          xAxisData,
          applicableDataSources
        ),
      error: (err: any) => {
        console.error(err);
        this.setNoDataChartOptions();
      },
    });
  }

  protected onExpandedStateChanged(expanded: boolean): void {
    super.onExpandedStateChanged(expanded);
    forceChartOptionsRerender(this, { clone: true });
  }
}
